import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Share2, Copy, CheckCircle2, Trash2, GripVertical,
  ChevronUp, ChevronDown, Search, X, Loader2, Pencil,
  ArrowUpFromLine, ArrowDownToLine
} from 'lucide-react';
import api from '../../api/auth.api';
import { CustomTimePicker } from '../common/CustomTimePicker';

/**
 * Truck Schedule — plan the truck driver's day (default: tomorrow) as a ROUTE
 * of stops. Each stop = a place + what happens there: pick up machines (from
 * the GPS list of what's at that place), drop off machines (from what's on the
 * truck at that point in the route), or both. Drag to reorder, then copy/share
 * the numbered list as a text; addresses ride inline (iOS Messages auto-links
 * them to Apple Maps); Shop/Farm get no address.
 */

interface FleetMachine {
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string | null;
  equipment_type: string | null;
  equipment_subtype?: string | null;
  is_rental?: boolean;
  rental_company?: string | null;
}

interface FleetGroup {
  job_id: number | null;
  job_name: string;
  bradanna_job_number: string | null;
  equipment: FleetMachine[];
}

interface Job {
  job_id: number;
  job_name: string;
  bradanna_job_number?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  lat?: number | null;
  lng?: number | null;
}

interface StopItem {
  equipment_id: number | null; // null = custom typed item
  label: string;
  tkey?: string; // machine type key — lets a drop+pickup of the same type read as a "swap"
}

interface Place {
  job_id: number | null; // null = custom place (e.g. "Cliff's house")
  label: string;
  address: string;
  lat: number | null;
  lng: number | null;
}

interface RouteEvent {
  id: string;
  place: Place;
  pickups: StopItem[];
  drops: StopItem[];
  note: string;
}

const SHOP_JOB_ID = 1;
// Shop (job 1) and Farm (job 2) — the driver knows these, keep the text clean
const NO_ADDRESS_JOB_IDS = new Set([1, 2]);
const isHomeBase = (jobId: number | null | undefined, name: string) =>
  (jobId != null && NO_ADDRESS_JOB_IDS.has(jobId)) || /^(the\s+)?(shop|farm)$/i.test((name || '').trim());
const DRAFT_KEY = 'bfa_truck_schedule_v1';

const dateKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayKey = () => dateKey(new Date());
const tomorrowKey = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return dateKey(d);
};
// Local-noon Date from a YYYY-MM-DD key, immune to UTC parsing off-by-one
const keyToDate = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d, 12);
};
const fmtScheduleDay = (key: string, long = false) =>
  keyToDate(key).toLocaleDateString('en-US', long
    ? { weekday: 'long', month: 'long', day: 'numeric' }
    : { weekday: 'short', month: 'numeric', day: 'numeric' });

// Makes that lead an equipment_name ("Cat CW16") get flipped behind the model
// ("CW16 Cat") — user-preferred reading order: model, make, type, number.
const MAKES = new Set([
  'cat', 'caterpillar', 'bobcat', 'kenworth', 'peterbilt', 'pete', 'komatsu',
  'deere', 'john deere', 'volvo', 'case', 'kubota', 'takeuchi', 'wacker',
  'wacker neuson', 'hamm', 'weiler', 'laymor', 'mack', 'ford', 'dynapac',
  'sakai', 'jcb', 'genie', 'jlg', 'doosan', 'develon', 'hitachi', 'terex',
  'vermeer', 'toro', 'gehl', 'new holland', 'link-belt', 'ditch witch'
]);

// Type names the driver knows by a shorter handle
const TYPE_SHORT: Record<string, string> = {
  'mini excavator': 'Mini Ex'
};

const machineLabel = (
  m: { equipment_name: string; bradanna_equipment_number?: string | null; equipment_type?: string | null },
  includeNum: boolean
) => {
  const num = (m.bradanna_equipment_number || '').trim();
  let name = (m.equipment_name || '').trim();
  const type = (m.equipment_type || '').trim();
  // Pull a unit number baked into the front of the name so it can move to the end
  if (num) {
    if (name.toLowerCase() === num.toLowerCase()) name = '';
    else if (name.toLowerCase().startsWith(num.toLowerCase() + ' ')) name = name.slice(num.length).trim();
  }
  // "Cat CW16" → "CW16 Cat"
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length >= 3 && MAKES.has(`${words[0]} ${words[1]}`.toLowerCase())) {
    name = [...words.slice(2), words[0], words[1]].join(' ');
  } else if (words.length >= 2 && MAKES.has(words[0].toLowerCase())) {
    name = [...words.slice(1), words[0]].join(' ');
  }
  // "303ECR" → "303": drop letters trailing a digits-first model word.
  // Also drop "Cat" — nearly the whole fleet is Cat, so it's just noise.
  name = name.split(/\s+/)
    .filter(w => !/^(cat|caterpillar)$/i.test(w))
    .map(w => {
      const mm = w.match(/^(\d{2,})[A-Za-z]+$/);
      return mm ? mm[1] : w;
    }).join(' ');
  const parts: string[] = [];
  if (name) parts.push(name);
  if (type && !name.toLowerCase().includes(type.toLowerCase())) {
    parts.push(TYPE_SHORT[type.toLowerCase()] || type);
  }
  if (includeNum && num && !parts.join(' ').toLowerCase().includes(num.toLowerCase())) parts.push(num);
  return parts.join(' ');
};

// Group machines by type (name as fallback) so the number is only shown when
// two of the same kind sit at the same pickup spot.
const machineKey = (m: { equipment_name: string; equipment_type?: string | null }) =>
  (m.equipment_type || '').trim().toLowerCase() || (m.equipment_name || '').trim().toLowerCase();

const jobAddress = (j: Job | undefined | null): string => {
  if (!j) return '';
  const parts = [j.address, j.city, j.state].filter(Boolean);
  let s = parts.join(', ');
  if (s && j.zip_code) s += ` ${j.zip_code}`;
  return s;
};

const mapsUrl = (address?: string, lat?: number | null, lng?: number | null): string => {
  if (address) return `https://maps.apple.com/?q=${encodeURIComponent(address)}`;
  if (lat != null && lng != null) return `https://maps.apple.com/?ll=${lat},${lng}`;
  return '';
};

const itemKey = (it: StopItem) =>
  it.equipment_id != null ? `e${it.equipment_id}` : `c${it.label.toLowerCase()}`;

// ---- Route math: miles + timeline estimates ----
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoiY2xpZmZpc29uIiwiYSI6ImNtNHZqZ3p1cTA3MmcycHE2bDdtdmlwdmYifQ.6kIxcCZXH6I9gHe41FhkMw';
const LOWBOY_TIME_FACTOR = 1.15; // lowboy runs slower than the car profile
const ROAD_FACTOR = 1.25; // straight-line → road miles (fallback while Mapbox loads)
const AVG_MPH = 35;       // truck + lowboy average (fallback)
const SHOP_PREP_MIN = 10;  // getting the truck ready at the shop each morning
const STOP_MIN = 15;       // no stop is quicker than this, even one small machine
const SMALL_RE = /(mini|skid|broom|box|plate|attachment|tractor)/i;
const BIG_EX_RE = /excavator/i; // full-size excavators (336...) take 30 to chain down
const minutesForItem = (it: StopItem) => {
  const k = it.tkey || it.label;
  if (SMALL_RE.test(k)) return 10;
  if (BIG_EX_RE.test(k)) return 30;
  return 15;
};
const stopMinutes = (ev: RouteEvent) =>
  Math.max(STOP_MIN, [...ev.drops, ...ev.pickups].reduce((s, it) => s + minutesForItem(it), 0));
const haversineMi = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const R = 3958.8;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};
const addMin = (d: Date, m: number) => new Date(d.getTime() + m * 60000);
const fmtClock = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const placeKeyOf = (p: Place) =>
  p.job_id != null ? `j${p.job_id}` : `n${p.label.trim().toLowerCase()}`;

// What's on the truck just before events[uptoIndex]
const truckAt = (events: RouteEvent[], uptoIndex: number): StopItem[] => {
  const inv = new Map<string, StopItem>();
  events.slice(0, uptoIndex).forEach(ev => {
    ev.pickups.forEach(it => inv.set(itemKey(it), it));
    ev.drops.forEach(it => inv.delete(itemKey(it)));
  });
  return [...inv.values()];
};

// Route physics check: walking the proposed order, every drop must be of
// something on the truck, and every pickup must be of something that's
// actually at that stop. Returns a human-readable problem, or null if valid.
const orderProblem = (evs: RouteEvent[]): string | null => {
  const state = new Map<string, string>(); // itemKey -> 'truck' | placeKey
  for (const ev of evs) {
    const pk = placeKeyOf(ev.place);
    for (const it of ev.drops) {
      if (state.get(itemKey(it)) !== 'truck') {
        return `${it.label} would be dropped at ${ev.place.label} before it's on the truck`;
      }
      state.set(itemKey(it), pk);
    }
    for (const it of ev.pickups) {
      const st = state.get(itemKey(it));
      if (st === 'truck') return `${it.label} would already be on the truck at ${ev.place.label}`;
      if (st !== undefined && st !== pk) return `${it.label} wouldn't be at ${ev.place.label} at that point in the route`;
      state.set(itemKey(it), 'truck');
    }
  }
  return null;
};

// Stops that share machines form one movable unit: each pickup links to the
// drop that unloads it (union-find). Reordering moves whole groups so a drop
// can never be separated from its pickup.
const computeGroups = (evs: RouteEvent[]): string[][] => {
  const parent = evs.map((_, i) => i);
  const find = (x: number): number => (parent[x] === x ? x : (parent[x] = find(parent[x])));
  const union = (a: number, b: number) => { parent[find(a)] = find(b); };
  const open = new Map<string, number>(); // itemKey -> stop index that picked it up
  evs.forEach((ev, i) => {
    ev.drops.forEach(it => {
      const k = itemKey(it);
      const p = open.get(k);
      if (p !== undefined) { union(p, i); open.delete(k); }
    });
    ev.pickups.forEach(it => { open.set(itemKey(it), i); });
  });
  const byRoot = new Map<number, string[]>();
  const order: number[] = [];
  evs.forEach((ev, i) => {
    const r = find(i);
    if (!byRoot.has(r)) { byRoot.set(r, []); order.push(r); }
    byRoot.get(r)!.push(ev.id);
  });
  return order.map(r => byRoot.get(r)!);
};

// Old drafts stored pickup→destination pairs; expand each into two route events
const migrateOldStops = (stops: any[]): RouteEvent[] =>
  stops.flatMap((s: any) => {
    const items: StopItem[] = Array.isArray(s.items) ? s.items : [];
    return [
      {
        id: `${s.id}_p`,
        place: {
          job_id: s.from_job_id ?? null, label: s.from_label || 'Unknown',
          address: s.from_address || '', lat: s.from_lat ?? null, lng: s.from_lng ?? null
        },
        pickups: items, drops: [], note: ''
      },
      {
        id: `${s.id}_d`,
        place: {
          job_id: s.to_job_id ?? null, label: s.to_label || 'Unknown',
          address: s.to_address || '', lat: s.to_lat ?? null, lng: s.to_lng ?? null
        },
        pickups: [], drops: items, note: s.note || ''
      }
    ];
  });

type ModalStep = 'place' | 'what' | 'edit';

export const TruckSchedule: React.FC = () => {
  const navigate = useNavigate();

  const [groups, setGroups] = useState<FleetGroup[]>([]);
  const [fleetError, setFleetError] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [allEquipment, setAllEquipment] = useState<FleetMachine[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [events, setEvents] = useState<RouteEvent[]>([]);
  const [scheduleFor, setScheduleFor] = useState<string>(tomorrowKey());
  const [startTime, setStartTime] = useState<string>(() => {
    try { return localStorage.getItem('bfa_truck_sched_start') || '04:30'; } catch { return '04:30'; }
  });
  useEffect(() => {
    try { localStorage.setItem('bfa_truck_sched_start', startTime); } catch { /* non-fatal */ }
  }, [startTime]);
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const [orderWarn, setOrderWarn] = useState<string | null>(null);
  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warn = (msg: string) => {
    setOrderWarn(msg);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    warnTimerRef.current = setTimeout(() => setOrderWarn(null), 3500);
  };

  // Add/edit modal state
  const [modalStep, setModalStep] = useState<ModalStep | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [placeSel, setPlaceSel] = useState<Place | null>(null);
  const [pickupSel, setPickupSel] = useState<Record<number, string>>({});
  const [customPickups, setCustomPickups] = useState<string[]>([]);
  const [dropSel, setDropSel] = useState<Record<string, StopItem>>({});
  const [customText, setCustomText] = useState('');
  const [customPlaceName, setCustomPlaceName] = useState('');
  const [customPlaceAddr, setCustomPlaceAddr] = useState('');
  const [showAllPlaces, setShowAllPlaces] = useState(false);
  const [showAllEquip, setShowAllEquip] = useState(false);
  const [placeSearch, setPlaceSearch] = useState('');
  const [stopNote, setStopNote] = useState('');

  // ---- Data load ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const jobsRes = await api.get('/api/jobs');
        if (cancelled) return;
        setJobs(jobsRes.data?.data || []);
      } catch (err) {
        console.error('Jobs load error:', err);
        if (!cancelled) setLoadError('Could not load jobs. Pull down to retry or check your connection.');
      }
      try {
        const fleetRes = await api.get('/api/fleet/locations');
        if (cancelled) return;
        setGroups(fleetRes.data?.data?.groups || []);
      } catch (err) {
        console.error('Fleet load error:', err);
        if (!cancelled) setFleetError(true);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // ---- Server persistence: the schedule lives in TRUCK_SCHEDULES so the PWA
  // and bfav5 always show the same route for a given day ----
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const loadedForRef = useRef<string | null>(null);
  const skipSaveRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef(false);

  const loadSchedule = useCallback(async (date: string) => {
    loadedForRef.current = null;
    try {
      const r = await api.get(`/api/truck-schedule/${date}`);
      let evs: RouteEvent[] = r.data?.data?.events || [];
      // One-time adoption of a pre-server local draft for this date
      let adopted = false;
      if (evs.length === 0) {
        try {
          const raw = localStorage.getItem(DRAFT_KEY);
          if (raw) {
            const draft = JSON.parse(raw);
            const forDate = draft?.forDate || draft?.date;
            if (forDate === date) {
              if (Array.isArray(draft.events)) evs = draft.events;
              else if (Array.isArray(draft.stops)) evs = migrateOldStops(draft.stops);
              localStorage.removeItem(DRAFT_KEY);
              adopted = evs.length > 0;
            }
          }
        } catch { /* ignore corrupt draft */ }
      }
      skipSaveRef.current = true;
      setEvents(evs);
      loadedForRef.current = date;
      if (adopted) {
        api.put(`/api/truck-schedule/${date}`, { events: evs }).catch(() => {});
      }
    } catch (err) {
      console.error('Schedule load error:', err);
      setLoadError('Could not load the saved schedule — check your connection and reopen.');
    }
  }, []);

  useEffect(() => { loadSchedule(scheduleFor); }, [scheduleFor, loadSchedule]);

  // Debounced autosave of edits to the server (last write wins)
  useEffect(() => {
    if (skipSaveRef.current) { skipSaveRef.current = false; return; }
    const date = loadedForRef.current;
    if (!date) return;
    setSaveState('saving');
    pendingRef.current = true;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    const payload = events;
    saveTimerRef.current = setTimeout(async () => {
      try {
        await api.put(`/api/truck-schedule/${date}`, { events: payload });
        pendingRef.current = false;
        setSaveState('saved');
      } catch (err) {
        console.error('Schedule save error:', err);
        setSaveState('error');
      }
    }, 600);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [events]);

  // Re-pull on app resume so phone and desktop stay in sync (installed PWAs
  // skip visibilitychange on reopen — listen for focus + pageshow)
  useEffect(() => {
    const onWake = () => {
      if (loadedForRef.current && !pendingRef.current) loadSchedule(loadedForRef.current);
    };
    window.addEventListener('focus', onWake);
    window.addEventListener('pageshow', onWake);
    return () => {
      window.removeEventListener('focus', onWake);
      window.removeEventListener('pageshow', onWake);
    };
  }, [loadSchedule]);

  const jobById = useMemo(() => {
    const m = new Map<number, Job>();
    jobs.forEach(j => m.set(j.job_id, j));
    return m;
  }, [jobs]);

  const coordsFor = useCallback((p: Place): { lat: number; lng: number } | null => {
    if (p.lat != null && p.lng != null) return { lat: p.lat, lng: p.lng };
    if (p.job_id != null) {
      const j = jobById.get(p.job_id);
      if (j?.lat != null && j?.lng != null) return { lat: j.lat, lng: j.lng };
    }
    return null;
  }, [jobById]);

  // Waypoint chain: Shop start + every stop with coords. stopLeg[i] = which
  // road leg ends at stop i (null = no coords, leg unmeasurable).
  const waypoints = useMemo(() => {
    const pts: { lat: number; lng: number }[] = [];
    const stopLeg: (number | null)[] = [];
    const shop = jobById.get(SHOP_JOB_ID);
    if (shop?.lat != null && shop?.lng != null) pts.push({ lat: shop.lat, lng: shop.lng });
    events.forEach(ev => {
      const c = coordsFor(ev.place);
      if (c) {
        pts.push(c);
        stopLeg.push(pts.length >= 2 ? pts.length - 2 : null);
      } else {
        stopLeg.push(null);
      }
    });
    const key = pts.map(c => `${c.lng.toFixed(5)},${c.lat.toFixed(5)}`).join(';');
    return { pts, stopLeg, key };
  }, [events, coordsFor, jobById]);

  // Real road miles/minutes from Mapbox Directions — one request per waypoint
  // sequence, debounced so drag-reordering doesn't spam the API. Haversine
  // fallback covers the gap while loading or on failure.
  const [roadLegs, setRoadLegs] = useState<{ key: string; legs: { mi: number; min: number }[] } | null>(null);
  useEffect(() => {
    const { pts, key } = waypoints;
    if (pts.length < 2 || pts.length > 25 || roadLegs?.key === key) return;
    const timer = setTimeout(async () => {
      try {
        const coordsStr = pts.map(c => `${c.lng.toFixed(6)},${c.lat.toFixed(6)}`).join(';');
        const res = await fetch(
          `https://api.mapbox.com/directions/v5/mapbox/driving/${coordsStr}?overview=false&access_token=${MAPBOX_TOKEN}`
        );
        const data = await res.json();
        const legs = data?.routes?.[0]?.legs;
        if (Array.isArray(legs) && legs.length === pts.length - 1) {
          setRoadLegs({
            key,
            legs: legs.map((l: any) => ({ mi: l.distance / 1609.34, min: (l.duration / 60) * LOWBOY_TIME_FACTOR }))
          });
        }
      } catch (err) {
        console.error('Mapbox directions error:', err);
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [waypoints, roadLegs]);

  // Miles + timeline: day starts at the Shop, drives stop to stop, spends
  // 10 min per small machine / 15 per big one at each stop
  const routePlan = useMemo(() => {
    const road = roadLegs?.key === waypoints.key ? roadLegs.legs : null;
    const shop = jobById.get(SHOP_JOB_ID);
    let prev = shop?.lat != null && shop?.lng != null ? { lat: shop.lat, lng: shop.lng } : null;
    const [sh, sm] = startTime.split(':').map(Number);
    let t = new Date();
    t.setHours(Number.isFinite(sh) ? sh : 4, Number.isFinite(sm) ? sm : 30, 0, 0);
    t = addMin(t, SHOP_PREP_MIN); // morning prep at the shop before rolling
    let totalMi = 0;
    let unknown = false;
    const perStop = events.map((ev, i) => {
      const c = coordsFor(ev.place);
      const legIdx = waypoints.stopLeg[i];
      let legMi: number | null = null;
      if (legIdx != null && road && road[legIdx]) {
        legMi = road[legIdx].mi;
        totalMi += legMi;
        t = addMin(t, road[legIdx].min);
      } else if (c && prev) {
        legMi = haversineMi(prev, c) * ROAD_FACTOR;
        totalMi += legMi;
        t = addMin(t, (legMi / AVG_MPH) * 60);
      } else if (!c) {
        unknown = true;
      }
      if (c) prev = c;
      const arrive = new Date(t);
      const handleMin = stopMinutes(ev);
      t = addMin(t, handleMin);
      return { legMi, arrive, handleMin };
    });
    return { totalMi, perStop, done: new Date(t), unknown, road: !!road };
  }, [events, startTime, jobById, coordsFor, waypoints, roadLegs]);

  // Shortlist: fleet groups that have equipment, shop first, Unknown last
  const pickupGroups = useMemo(() => {
    const withEquip = groups.filter(g => (g.equipment || []).length > 0);
    return withEquip.sort((a, b) => {
      if (a.job_id === SHOP_JOB_ID) return -1;
      if (b.job_id === SHOP_JOB_ID) return 1;
      if (a.job_id == null) return 1;
      if (b.job_id == null) return -1;
      return (a.job_name || '').localeCompare(b.job_name || '');
    });
  }, [groups]);

  const sortedJobs = useMemo(() =>
    [...jobs].sort((a, b) => {
      if (a.job_id === SHOP_JOB_ID) return -1;
      if (b.job_id === SHOP_JOB_ID) return 1;
      return (a.job_name || '').localeCompare(b.job_name || '');
    }), [jobs]);

  const filterJobs = (list: Job[], q: string) => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(j =>
      (j.job_name || '').toLowerCase().includes(s) ||
      (j.bradanna_job_number || '').toLowerCase().includes(s) ||
      (j.address || '').toLowerCase().includes(s) ||
      (j.city || '').toLowerCase().includes(s)
    );
  };

  const loadAllEquipment = useCallback(async () => {
    if (allEquipment) return;
    try {
      const res = await api.get('/api/equipment');
      const rows = res.data?.data || res.data || [];
      setAllEquipment(rows);
    } catch (err) {
      console.error('Equipment load error:', err);
      setAllEquipment([]);
    }
  }, [allEquipment]);

  // ---- Add/edit flow ----
  const resetModalDraft = () => {
    setPlaceSel(null);
    setPickupSel({});
    setCustomPickups([]);
    setDropSel({});
    setCustomText('');
    setCustomPlaceName('');
    setCustomPlaceAddr('');
    setShowAllPlaces(false);
    setShowAllEquip(false);
    setPlaceSearch('');
    setStopNote('');
  };

  const openAddStop = () => {
    resetModalDraft();
    setEditingId(null);
    setModalStep('place');
  };

  const openEditStop = (ev: RouteEvent) => {
    resetModalDraft();
    setEditingId(ev.id);
    setPlaceSel(ev.place);
    setPickupSel(Object.fromEntries(
      ev.pickups.filter(it => it.equipment_id != null).map(it => [it.equipment_id as number, it.label])
    ));
    setCustomPickups(ev.pickups.filter(it => it.equipment_id == null).map(it => it.label));
    setDropSel(Object.fromEntries(ev.drops.map(it => [itemKey(it), it])));
    setStopNote(ev.note || '');
    setModalStep('edit');
  };

  const closeModal = () => {
    setModalStep(null);
    setEditingId(null);
  };

  const pickPlace = (job_id: number | null, label: string) => {
    const j = job_id != null ? jobById.get(job_id) : null;
    const home = isHomeBase(job_id, label);
    // Home bases skip the address (driver knows them) but KEEP coords for mileage
    setPlaceSel({
      job_id, label,
      address: home ? '' : jobAddress(j),
      lat: j?.lat ?? null,
      lng: j?.lng ?? null
    });
    if (!editingId) { setPickupSel({}); setDropSel({}); }
    setShowAllEquip(false);
    setModalStep(editingId ? 'edit' : 'what');
  };

  const useCustomPlace = () => {
    const name = customPlaceName.trim();
    if (!name) return;
    setPlaceSel({ job_id: null, label: name, address: customPlaceAddr.trim(), lat: null, lng: null });
    if (!editingId) { setPickupSel({}); setDropSel({}); }
    setShowAllEquip(false);
    setModalStep(editingId ? 'edit' : 'what');
  };

  const addCustomItem = () => {
    const t = customText.trim();
    if (!t) return;
    setCustomPickups(prev => [...prev, t]);
    setCustomText('');
  };

  const toggleDrop = (it: StopItem) => {
    setDropSel(prev => {
      const next = { ...prev };
      const k = itemKey(it);
      if (next[k]) delete next[k];
      else next[k] = it;
      return next;
    });
  };

  const pickupCount = Object.keys(pickupSel).length + customPickups.length;
  const dropCount = Object.keys(dropSel).length;
  const actionCount = pickupCount + dropCount;

  const tkeyForId = (id: number, label: string): string => {
    for (const g of groups) {
      const m = (g.equipment || []).find(x => x.equipment_id === id);
      if (m) return machineKey(m);
    }
    const m2 = (allEquipment || []).find(x => x.equipment_id === id);
    if (m2) return machineKey(m2);
    const ex = droppedHereExtras.find(x => x.equipment_id === id);
    if (ex?.tkey) return ex.tkey;
    return label.trim().toLowerCase();
  };

  const buildPickups = (): StopItem[] => [
    ...Object.entries(pickupSel).map(([id, label]) => ({
      equipment_id: Number(id), label, tkey: tkeyForId(Number(id), label)
    })),
    ...customPickups.map(label => ({ equipment_id: null, label, tkey: label.trim().toLowerCase() }))
  ];

  const saveStop = (andNext = false) => {
    if (!placeSel || actionCount === 0) return;
    const data = {
      place: placeSel,
      pickups: buildPickups(),
      drops: Object.values(dropSel),
      note: stopNote.trim()
    };
    const proposed = editingId
      ? events.map(ev => ev.id === editingId ? { ...ev, ...data } : ev)
      : [...events, { id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, ...data }];
    const prob = orderProblem(proposed);
    if (prob) {
      window.alert(`This doesn't work with the rest of the route: ${prob}.`);
      return;
    }
    setEvents(proposed);
    if (andNext && !editingId) {
      resetModalDraft();
      setModalStep('place');
    } else {
      closeModal();
    }
  };

  const removeStop = (id: string) => {
    const next = events.filter(ev => ev.id !== id);
    const prob = orderProblem(next);
    if (prob) {
      if (!window.confirm(`Deleting this stop breaks the route: ${prob}. Delete anyway?`)) return;
    }
    setEvents(next);
  };

  // Crossing a stop in the SAME group = plain swap inside the group (e.g. two
  // drops of the same load trading order). Crossing into a different group =
  // the whole pickup+drop unit moves past that group together.
  const moveStop = (index: number, dir: -1 | 1) => {
    const id = events[index]?.id;
    if (!id) return;
    const j = index + dir;
    const groups = computeGroups(events);
    const gi = groups.findIndex(g => g.includes(id));
    if (j >= 0 && j < events.length && groups[gi]?.includes(events[j].id)) {
      const next = [...events];
      [next[index], next[j]] = [next[j], next[index]];
      const prob = orderProblem(next);
      if (prob) { warn(`Can't move: ${prob}.`); return; }
      setEvents(next);
      return;
    }
    const gj = gi + dir;
    if (gj < 0 || gj >= groups.length) return;
    const newGroups = [...groups];
    [newGroups[gi], newGroups[gj]] = [newGroups[gj], newGroups[gi]];
    const byId = new Map(events.map(ev => [ev.id, ev]));
    const next = newGroups.flat().map(eid => byId.get(eid)!);
    const prob = orderProblem(next);
    if (prob) { warn(`Can't move: ${prob}.`); return; }
    setEvents(next);
  };

  const canMove = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= events.length) return false;
    const gi = groupIdxOf(events[i].id);
    if (evGroups[gi]?.includes(events[j].id)) return true;
    return dir === -1 ? gi > 0 : gi < evGroups.length - 1;
  };

  // What's on the truck at the point this modal is working (end of route for
  // add; just before the event for edit — plus its own drops, they were aboard)
  const truckInventory = useMemo(() => {
    if (editingId) {
      const idx = events.findIndex(ev => ev.id === editingId);
      if (idx === -1) return [];
      const inv = truckAt(events, idx);
      const have = new Set(inv.map(itemKey));
      events[idx].drops.forEach(it => { if (!have.has(itemKey(it))) inv.push(it); });
      return inv;
    }
    return truckAt(events, events.length);
  }, [events, editingId]);

  // ---- Keep the bottom sheet above the iOS keyboard ----
  // Fixed elements anchor to the layout viewport, which the iOS keyboard
  // overlays; size the overlay to the visual viewport instead.
  const [vv, setVv] = useState({ h: window.innerHeight, top: 0 });
  useEffect(() => {
    const vvp = window.visualViewport;
    if (!vvp) return;
    const update = () => setVv({ h: vvp.height, top: vvp.offsetTop });
    vvp.addEventListener('resize', update);
    vvp.addEventListener('scroll', update);
    update();
    return () => {
      vvp.removeEventListener('resize', update);
      vvp.removeEventListener('scroll', update);
    };
  }, []);

  const evGroups = useMemo(() => computeGroups(events), [events]);
  const groupIdxOf = (id: string) => evGroups.findIndex(g => g.includes(id));

  // ---- Drag to reorder (drags the whole pickup+drop group) ----
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const eventsRef = useRef(events);
  eventsRef.current = events;

  const onDragStart = (e: React.PointerEvent, id: string) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDraggingId(id);
  };

  const onDragMove = (e: React.PointerEvent, id: string) => {
    if (draggingId !== id) return;
    const y = e.clientY;
    const current = eventsRef.current;
    const fromIdx = current.findIndex(ev => ev.id === id);
    if (fromIdx === -1) return;
    let toIdx = fromIdx;
    for (let i = 0; i < current.length; i++) {
      if (current[i].id === id) continue;
      const el = rowRefs.current.get(current[i].id);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const mid = r.top + r.height / 2;
      if (i < fromIdx && y < mid) { toIdx = Math.min(toIdx, i); }
      if (i > fromIdx && y > mid) { toIdx = Math.max(toIdx, i); }
    }
    if (toIdx !== fromIdx) {
      const groups = computeGroups(current);
      const gi = groups.findIndex(g => g.includes(id));
      const gt = groups.findIndex(g => g.includes(current[toIdx].id));
      if (gi === -1 || gt === -1) return;
      if (gt === gi) {
        // within the same group: reposition just this stop
        const next = [...current];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        const prob = orderProblem(next);
        if (prob) { warn(`Can't move: ${prob}.`); return; }
        setEvents(next);
        return;
      }
      const newGroups = [...groups];
      const [g] = newGroups.splice(gi, 1);
      newGroups.splice(gt, 0, g);
      const byId = new Map(current.map(ev => [ev.id, ev]));
      const next = newGroups.flat().map(eid => byId.get(eid)!);
      const prob = orderProblem(next);
      if (prob) { warn(`Can't move: ${prob}.`); return; }
      setEvents(next);
    }
  };

  const onDragEnd = () => setDraggingId(null);

  const inDragGroup = (id: string) => {
    if (!draggingId) return false;
    const g = evGroups.find(gr => gr.includes(draggingId));
    return g ? g.includes(id) : draggingId === id;
  };

  // ---- Message + share ----
  // One plain sentence per stop — driver-proof wording.
  const message = useMemo(() => {
    if (events.length === 0) return '';
    const dayStr = fmtScheduleDay(scheduleFor);
    const joinAnd = (items: string[]) =>
      items.length <= 1 ? (items[0] || '')
        : items.length === 2 ? `${items[0]} and ${items[1]}`
        : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
    const keyOf = (it: StopItem) => it.tkey || it.label.trim().toLowerCase();
    // First three words of the job name. "&"/"and" glue their neighbors ("B & I"
    // stays whole); dangling "at/of/the" gets trimmed ("Sephora at" → "Sephora").
    const shortPlace = (label: string) => {
      const words = label.trim().split(/\s+/);
      const GLUE = /^(&|and|\+|-|–|—)$/i;
      const TRAIL = /^(at|of|the|on|@)$/i;
      let n = Math.min(3, words.length);
      if (n < words.length && GLUE.test(words[n - 1])) n++;
      else if (n < words.length && GLUE.test(words[n])) n = Math.min(n + 2, words.length);
      while (n > 1 && TRAIL.test(words[n - 1])) n--;
      return words.slice(0, n).join(' ');
    };
    const lines = events.map((ev, i) => {
      const loc = shortPlace(ev.place.label) + (ev.place.address ? `, ${ev.place.address}` : '');
      // Pair same-type drop+pickup at one stop into "swap" phrasing
      const drops = [...ev.drops];
      const picks = [...ev.pickups];
      const swaps: string[] = [];
      for (let d = drops.length - 1; d >= 0; d--) {
        const pi = picks.findIndex(p => keyOf(p) === keyOf(drops[d]));
        if (pi !== -1) {
          const dropIt = drops[d];
          const pickIt = picks[pi];
          swaps.unshift(dropIt.label === pickIt.label
            ? `swap ${dropIt.label}`
            : `swap ${dropIt.label} for ${pickIt.label}`);
          picks.splice(pi, 1);
          drops.splice(d, 1);
        }
      }
      const parts: string[] = [...swaps];
      if (drops.length > 0) parts.push(`drop off ${joinAnd(drops.map(it => it.label))}`);
      if (picks.length > 0) parts.push(`pick up ${joinAnd(picks.map(it => it.label))}`);
      let sentence = parts.join(' and ');
      sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1);
      let line = `${i + 1}. ${sentence} at ${loc}`;
      if (ev.note) line += `\nNote: ${ev.note}`;
      return line;
    });
    return `Truck Schedule — ${dayStr}\n\n${lines.join('\n')}`;
  }, [events, scheduleFor]);

  const handleCopy = async () => {
    if (!message) return;
    try {
      await navigator.clipboard.writeText(message);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = message;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (!message) return;
    if (navigator.share) {
      try {
        await navigator.share({ text: message });
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      } catch (err: any) {
        if (err.name !== 'AbortError') handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  // ---- Modal equipment list ----
  const groupForPlace = useMemo(() => {
    if (!placeSel) return null;
    return groups.find(g => g.job_id === placeSel.job_id && (placeSel.job_id != null || g.job_name === placeSel.label)) || null;
  }, [groups, placeSel]);

  // Where each GPS-tracked machine ends up through the route, up to the point
  // this modal is inserting (end of route) or editing (just before the event):
  // 'truck', or the key of the place it was dropped at.
  const routeStateBefore = useMemo(() => {
    let upto = events.length;
    if (editingId) {
      const idx = events.findIndex(ev => ev.id === editingId);
      if (idx !== -1) upto = idx;
    }
    const state = new Map<number, { loc: string; item: StopItem }>();
    events.slice(0, upto).forEach(ev => {
      const pk = placeKeyOf(ev.place);
      ev.pickups.forEach(it => { if (it.equipment_id != null) state.set(it.equipment_id, { loc: 'truck', item: it }); });
      ev.drops.forEach(it => { if (it.equipment_id != null) state.set(it.equipment_id, { loc: pk, item: it }); });
    });
    return state;
  }, [events, editingId]);

  // A machine is offered for pickup here only if the route hasn't moved it
  // (still where GPS put it) or the route dropped it back at this very place.
  const equipChoices: FleetMachine[] = useMemo(() => {
    const base = showAllEquip ? (allEquipment || []) : (groupForPlace?.equipment || []);
    if (!placeSel) return base;
    const pk = placeKeyOf(placeSel);
    return base.filter(m => {
      const st = routeStateBefore.get(m.equipment_id);
      return st === undefined || st.loc === pk;
    });
  }, [showAllEquip, allEquipment, groupForPlace, placeSel, routeStateBefore]);

  // Machines the route dropped at this place earlier that GPS doesn't list here
  const droppedHereExtras = useMemo(() => {
    if (!placeSel) return [] as StopItem[];
    const pk = placeKeyOf(placeSel);
    const baseIds = new Set(equipChoices.map(m => m.equipment_id));
    const extras: StopItem[] = [];
    routeStateBefore.forEach((st, id) => {
      if (st.loc === pk && !baseIds.has(id)) extras.push(st.item);
    });
    return extras;
  }, [placeSel, routeStateBefore, equipChoices]);

  const toggleExtraPickup = (it: StopItem) => {
    const id = it.equipment_id as number;
    setPickupSel(prev => {
      const next = { ...prev };
      if (next[id] !== undefined) delete next[id];
      else next[id] = it.label;
      return next;
    });
  };

  const equipDupCounts = useMemo(() => {
    const counts = new Map<string, number>();
    equipChoices.forEach(m => counts.set(machineKey(m), (counts.get(machineKey(m)) || 0) + 1));
    return counts;
  }, [equipChoices]);

  const labelFor = (m: FleetMachine) => machineLabel(m, (equipDupCounts.get(machineKey(m)) || 0) > 1);

  const toggleEquip = (m: FleetMachine) => {
    setPickupSel(prev => {
      const next = { ...prev };
      if (next[m.equipment_id] !== undefined) delete next[m.equipment_id];
      else next[m.equipment_id] = labelFor(m);
      return next;
    });
  };

  // ---- Render helpers ----
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: 8, border: '2px solid #e5e7eb',
    fontSize: 16, fontFamily: 'inherit', boxSizing: 'border-box'
  };

  const linkStyle: React.CSSProperties = {
    color: '#2563eb', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2
  };

  const sectionLabel: React.CSSProperties = {
    fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
    letterSpacing: 0.5, margin: '14px 0 8px'
  };

  const jobRow = (name: string, sub: string, onClick: () => void, key: React.Key, badge?: string) => (
    <button key={key} onClick={onClick} style={{
      width: '100%', textAlign: 'left', background: 'white', border: '1px solid #e5e7eb',
      borderRadius: 10, padding: '12px 14px', marginBottom: 8, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b' }}>{name}</div>
        {sub && <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{sub}</div>}
      </div>
      {badge && (
        <span style={{
          flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#5b8a8a',
          background: '#eef4f4', borderRadius: 999, padding: '4px 10px'
        }}>{badge}</span>
      )}
    </button>
  );

  const toggleRow = (label: string, sub: string, on: boolean, onClick: () => void, key: React.Key) => (
    <button key={key} onClick={onClick} style={{
      width: '100%', textAlign: 'left', background: on ? '#eef4f4' : 'white',
      border: on ? '2px solid #5b8a8a' : '1px solid #e5e7eb',
      borderRadius: 10, padding: '12px 14px', marginBottom: 8, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8
    }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b' }}>{label}</div>
        {sub && <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{sub}</div>}
      </div>
      {on && <CheckCircle2 size={20} color="#5b8a8a" style={{ flexShrink: 0 }} />}
    </button>
  );

  const secondaryBtn: React.CSSProperties = {
    width: '100%', padding: 12, borderRadius: 10, border: '1px solid #d1d5db',
    background: '#f8fafc', color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer'
  };

  const modalTitle = modalStep === 'edit' ? 'Edit stop'
    : modalStep === 'place' ? 'Where’s the stop?'
    : `What happens at ${placeSel?.label}?`;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', paddingBottom: 120 }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
        padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12
      }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 4 }}>
          <ArrowLeft size={24} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'white', margin: 0 }}>Truck Schedule</h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', margin: 0 }}>
            For {fmtScheduleDay(scheduleFor, true)}
          </p>
        </div>
        {events.length > 0 && (
          <button
            onClick={() => { if (window.confirm('Clear the whole schedule?')) setEvents([]); }}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', cursor: 'pointer', padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}
          >
            Clear
          </button>
        )}
      </div>

      <div style={{ padding: 16 }}>
        {/* Which day is this schedule for */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Schedule for:</span>
          {[
            { key: todayKey(), label: 'Today' },
            { key: tomorrowKey(), label: 'Tomorrow' }
          ].map(o => (
            <button
              key={o.key}
              onClick={() => setScheduleFor(o.key)}
              style={{
                padding: '8px 16px', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                border: scheduleFor === o.key ? '2px solid #5b8a8a' : '1px solid #d1d5db',
                background: scheduleFor === o.key ? '#eef4f4' : 'white',
                color: scheduleFor === o.key ? '#3f6363' : '#64748b'
              }}
            >
              {o.label}
            </button>
          ))}
          {scheduleFor !== todayKey() && scheduleFor !== tomorrowKey() && (
            <span style={{ fontSize: 13, fontWeight: 700, color: '#3f6363' }}>{fmtScheduleDay(scheduleFor)}</span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: saveState === 'error' ? '#dc2626' : '#94a3b8' }}>
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : saveState === 'error' ? 'Not saved!' : ''}
          </span>
        </div>

        {/* Miles + timeline summary */}
        {events.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
            padding: '10px 14px', borderRadius: 10, background: 'white',
            border: '1px solid #e5e7eb', marginBottom: 14, fontSize: 13, color: '#475569'
          }}>
            <span style={{ fontWeight: 600 }}>Start:</span>
            <CustomTimePicker
              value={startTime}
              onChange={v => setStartTime(v || '04:30')}
              label="Start"
              style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 6px', fontSize: 14, fontFamily: 'inherit' }}
            />
            <span><b>{Math.round(routePlan.totalMi)} mi</b> total {routePlan.road ? '(road)' : '(est.)'}</span>
            <span>done ~<b>{fmtClock(routePlan.done)}</b></span>
            {routePlan.unknown && (
              <span style={{ color: '#92400e' }}>some stops have no map location — miles are missing legs</span>
            )}
          </div>
        )}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', padding: 24, justifyContent: 'center' }}>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading jobs & equipment...
          </div>
        )}
        {loadError && (
          <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 14, marginBottom: 16 }}>
            {loadError}
          </div>
        )}
        {!loading && fleetError && (
          <div style={{ padding: 12, borderRadius: 8, backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 13, marginBottom: 16 }}>
            Live GPS equipment list unavailable — you can still build the schedule from the full job and equipment lists.
          </div>
        )}

        {orderWarn && (
          <div style={{
            padding: '10px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca',
            fontSize: 13, fontWeight: 600, color: '#dc2626', marginBottom: 10
          }}>
            {orderWarn}
          </div>
        )}

        {/* Route list */}
        {!loading && events.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 20px' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🚛</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#64748b' }}>No stops yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Tap "Add Stop" to plan the first stop of the day.</div>
          </div>
        )}

        {events.map((ev, i) => (
          <div
            key={ev.id}
            ref={el => { if (el) rowRefs.current.set(ev.id, el); else rowRefs.current.delete(ev.id); }}
            style={{
              background: 'white', borderRadius: 12, marginBottom: 10, padding: '12px 10px 12px 4px',
              display: 'flex', alignItems: 'stretch', gap: 4,
              border: inDragGroup(ev.id) ? '2px solid #5b8a8a' : '1px solid #e5e7eb',
              boxShadow: inDragGroup(ev.id) ? '0 8px 24px rgba(0,0,0,0.18)' : '0 1px 2px rgba(0,0,0,0.04)',
              transform: inDragGroup(ev.id) ? 'scale(1.02)' : 'none',
              transition: inDragGroup(ev.id) ? 'none' : 'transform 0.15s',
              position: 'relative', zIndex: inDragGroup(ev.id) ? 5 : 1
            }}
          >
            {/* Drag handle */}
            <div
              onPointerDown={e => onDragStart(e, ev.id)}
              onPointerMove={e => onDragMove(e, ev.id)}
              onPointerUp={onDragEnd}
              onPointerCancel={onDragEnd}
              style={{
                display: 'flex', alignItems: 'center', padding: '0 6px', color: '#94a3b8',
                touchAction: 'none', cursor: 'grab', userSelect: 'none'
              }}
            >
              <GripVertical size={20} />
            </div>

            {/* Body */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{
                  fontSize: 13, fontWeight: 800, color: 'white', background: '#5b8a8a',
                  borderRadius: 999, width: 22, height: 22, display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1
                }}>{i + 1}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                    {ev.place.label}
                    {ev.place.address && (
                      <>
                        {' '}
                        <a href={mapsUrl(ev.place.address, ev.place.lat, ev.place.lng)} target="_blank" rel="noopener noreferrer"
                          style={{ ...linkStyle, fontSize: 13, fontWeight: 600 }}>
                          {ev.place.address}
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {ev.drops.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  {ev.drops.map((it, li) => (
                    <div key={li} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, color: '#b45309', lineHeight: 1.5 }}>
                      <ArrowDownToLine size={14} style={{ flexShrink: 0 }} /> {it.label}
                    </div>
                  ))}
                </div>
              )}
              {ev.pickups.length > 0 && (
                <div style={{ marginTop: ev.drops.length > 0 ? 4 : 6 }}>
                  {ev.pickups.map((it, li) => (
                    <div key={li} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, color: '#166534', lineHeight: 1.5 }}>
                      <ArrowUpFromLine size={14} style={{ flexShrink: 0 }} /> {it.label}
                    </div>
                  ))}
                </div>
              )}
              {ev.note && <div style={{ fontSize: 13, color: '#92400e', marginTop: 4 }}>Note: {ev.note}</div>}
              {routePlan.perStop[i] && (
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 5 }}>
                  ~{fmtClock(routePlan.perStop[i].arrive)}
                  {routePlan.perStop[i].legMi != null && ` · ${routePlan.perStop[i].legMi! < 10 ? routePlan.perStop[i].legMi!.toFixed(1) : Math.round(routePlan.perStop[i].legMi!)} mi drive`}
                  {` · ${routePlan.perStop[i].handleMin} min on site`}
                </div>
              )}
            </div>

            {/* Row controls */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <button onClick={() => moveStop(i, -1)} disabled={!canMove(i, -1)}
                style={{ background: 'none', border: 'none', color: !canMove(i, -1) ? '#e2e8f0' : '#64748b', cursor: 'pointer', padding: 2 }}>
                <ChevronUp size={18} />
              </button>
              <button onClick={() => openEditStop(ev)}
                style={{ background: 'none', border: 'none', color: '#5b8a8a', cursor: 'pointer', padding: 2 }}>
                <Pencil size={16} />
              </button>
              <button onClick={() => removeStop(ev.id)}
                style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2 }}>
                <Trash2 size={16} />
              </button>
              <button onClick={() => moveStop(i, 1)} disabled={!canMove(i, 1)}
                style={{ background: 'none', border: 'none', color: !canMove(i, 1) ? '#e2e8f0' : '#64748b', cursor: 'pointer', padding: 2 }}>
                <ChevronDown size={18} />
              </button>
            </div>
          </div>
        ))}

        {/* What's still on the truck at end of route */}
        {truckAt(events, events.length).length > 0 && !modalStep && (
          <div style={{
            padding: '10px 14px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a',
            fontSize: 13, color: '#92400e', marginBottom: 10
          }}>
            Still on the truck at the end of the day: {truckAt(events, events.length).map(it => it.label).join(', ')}
          </div>
        )}

        {/* Add stop */}
        {!loading && (
          <button onClick={openAddStop} style={{
            width: '100%', padding: 14, borderRadius: 10, border: '2px dashed #94a3b8',
            background: 'white', color: '#475569', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 4
          }}>
            <Plus size={20} /> Add Stop
          </button>
        )}

        {/* Message preview */}
        {message && (
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                Message preview
              </label>
              <button onClick={handleCopy} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                borderRadius: 8, border: '1px solid #d1d5db',
                background: copied ? '#d1fae5' : 'white', color: copied ? '#059669' : '#374151',
                fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}>
                {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                {copied ? 'Copied!' : 'Copy Text'}
              </button>
            </div>
            <div style={{
              padding: 14, borderRadius: 8, backgroundColor: 'white', border: '2px solid #5b8a8a',
              fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', color: '#1e293b',
              wordBreak: 'break-word'
            }}>
              {message}
            </div>
          </div>
        )}
      </div>

      {/* Sticky share bar */}
      {events.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, padding: '12px 16px',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
          background: 'white', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, zIndex: 50
        }}>
          <button onClick={handleShare} style={{
            flex: 1, padding: 14, borderRadius: 10, border: 'none',
            background: shared ? '#059669' : '#1e293b', color: 'white',
            fontSize: 16, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
          }}>
            {shared ? <><CheckCircle2 size={20} /> Sent to Share</> : <><Share2 size={20} /> Text Schedule</>}
          </button>
          <button onClick={handleCopy} style={{
            padding: '14px 18px', borderRadius: 10, border: '1px solid #d1d5db',
            background: copied ? '#d1fae5' : 'white', color: copied ? '#059669' : '#374151',
            fontSize: 15, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6
          }}>
            {copied ? <CheckCircle2 size={18} /> : <Copy size={18} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}

      {/* Add/edit bottom sheet */}
      {modalStep && (
        <div
          style={{
            position: 'fixed', left: 0, right: 0, top: vv.top, height: vv.h,
            background: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', alignItems: 'flex-end'
          }}
          onClick={closeModal}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', width: '100%', borderRadius: '24px 24px 0 0',
              maxHeight: Math.round(vv.h * 0.92), display: 'flex', flexDirection: 'column',
              paddingBottom: 'env(safe-area-inset-bottom)'
            }}
          >
            {/* Sheet header */}
            <div style={{ padding: '16px 16px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
              {modalStep !== 'edit' && (editingId || modalStep !== 'place') && (
                <button
                  onClick={() => setModalStep(editingId ? 'edit' : 'place')}
                  style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 4 }}
                >
                  <ArrowLeft size={22} />
                </button>
              )}
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', margin: 0, flex: 1 }}>{modalTitle}</h2>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 4 }}>
                <X size={22} />
              </button>
            </div>

            <div style={{ overflowY: 'auto', padding: '8px 16px 16px', flex: 1 }}>
              {/* EDIT: summary with per-part change buttons */}
              {modalStep === 'edit' && (
                <>
                  <div style={{
                    background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10,
                    padding: '12px 14px', marginBottom: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Location
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', marginTop: 2 }}>
                        {placeSel?.label || '—'}
                      </div>
                      {placeSel?.address && (
                        <div style={{ fontSize: 13, color: '#64748b' }}>{placeSel.address}</div>
                      )}
                    </div>
                    <button onClick={() => setModalStep('place')} style={{
                      flexShrink: 0, padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db',
                      background: 'white', color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer'
                    }}>
                      Change
                    </button>
                  </div>

                  <div style={{
                    background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10,
                    padding: '12px 14px', marginBottom: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        At this stop
                      </div>
                      {Object.values(dropSel).map((it, li) => (
                        <div key={`d${li}`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 600, color: '#b45309', marginTop: 2 }}>
                          <ArrowDownToLine size={14} /> {it.label}
                        </div>
                      ))}
                      {[...Object.values(pickupSel), ...customPickups].map((l, li) => (
                        <div key={`p${li}`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 600, color: '#166534', marginTop: 2 }}>
                          <ArrowUpFromLine size={14} /> {l}
                        </div>
                      ))}
                      {actionCount === 0 && <div style={{ fontSize: 14, color: '#dc2626', marginTop: 2 }}>Nothing selected</div>}
                    </div>
                    <button onClick={() => setModalStep('what')} style={{
                      flexShrink: 0, padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db',
                      background: 'white', color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer'
                    }}>
                      Change
                    </button>
                  </div>

                  <input
                    value={stopNote}
                    onChange={e => setStopNote(e.target.value)}
                    placeholder="Note for this stop (optional)"
                    style={{ ...inputStyle, marginBottom: 4 }}
                  />
                </>
              )}

              {/* STEP 1: place */}
              {modalStep === 'place' && (
                <>
                  {!showAllPlaces && !fleetError && (
                    <>
                      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
                        Places with equipment right now (from GPS):
                      </div>
                      {pickupGroups.map(g => jobRow(
                        g.job_id === SHOP_JOB_ID ? 'Shop' : g.job_name,
                        jobAddress(g.job_id != null ? jobById.get(g.job_id) : null),
                        () => pickPlace(g.job_id, g.job_id === SHOP_JOB_ID ? 'Shop' : g.job_name),
                        `g_${g.job_id ?? 'none'}_${g.job_name}`,
                        `${g.equipment.length} machine${g.equipment.length === 1 ? '' : 's'}`
                      ))}
                      <button onClick={() => setShowAllPlaces(true)} style={secondaryBtn}>
                        Show all jobs
                      </button>
                    </>
                  )}
                  {(showAllPlaces || fleetError) && (
                    <>
                      <div style={{ position: 'relative', marginBottom: 10 }}>
                        <Search size={18} style={{ position: 'absolute', left: 12, top: 12, color: '#94a3b8' }} />
                        <input
                          value={placeSearch}
                          onChange={e => setPlaceSearch(e.target.value)}
                          placeholder="Search jobs..."
                          style={{ ...inputStyle, paddingLeft: 38 }}
                        />
                      </div>
                      {!fleetError && (
                        <button onClick={() => setShowAllPlaces(false)} style={{ ...secondaryBtn, padding: 10, fontSize: 13, marginBottom: 10 }}>
                          Back to places with equipment
                        </button>
                      )}
                      {filterJobs(sortedJobs, placeSearch).map(j => jobRow(
                        j.job_id === SHOP_JOB_ID ? 'Shop' : j.job_name,
                        jobAddress(j),
                        () => pickPlace(j.job_id, j.job_id === SHOP_JOB_ID ? 'Shop' : j.job_name),
                        `j_${j.job_id}`,
                        (groups.find(g => g.job_id === j.job_id)?.equipment.length || 0) > 0
                          ? `${groups.find(g => g.job_id === j.job_id)!.equipment.length} there now`
                          : undefined
                      ))}
                    </>
                  )}

                  {/* Custom place — somewhere that isn't a job (Cliff's house...) */}
                  <div style={sectionLabel}>Somewhere else</div>
                  <input
                    value={customPlaceName}
                    onChange={e => setCustomPlaceName(e.target.value)}
                    placeholder="Place name (Cliff's house, Kelly Tractor...)"
                    style={{ ...inputStyle, marginBottom: 8 }}
                  />
                  <input
                    value={customPlaceAddr}
                    onChange={e => setCustomPlaceAddr(e.target.value)}
                    placeholder="Address (optional)"
                    style={{ ...inputStyle, marginBottom: 8 }}
                  />
                </>
              )}

              {/* STEP 2: what happens here */}
              {modalStep === 'what' && (
                <>
                  {/* Drop off — what's on the truck at this point */}
                  {truckInventory.length > 0 && (
                    <>
                      <div style={{ ...sectionLabel, marginTop: 0, display: 'flex', alignItems: 'center', gap: 6, color: '#b45309' }}>
                        <ArrowDownToLine size={15} /> Drop off here
                      </div>
                      {truckInventory.map(it => toggleRow(
                        it.label, 'On the truck',
                        !!dropSel[itemKey(it)],
                        () => toggleDrop(it),
                        `drop_${itemKey(it)}`
                      ))}
                      {truckInventory.length > 1 && (
                        <button
                          onClick={() => setDropSel(Object.fromEntries(truckInventory.map(it => [itemKey(it), it])))}
                          style={{ ...secondaryBtn, padding: 10, fontSize: 13, marginBottom: 4 }}
                        >
                          Drop off everything
                        </button>
                      )}
                    </>
                  )}

                  {/* Pick up — what GPS says is at this place */}
                  <div style={{ ...sectionLabel, marginTop: truckInventory.length > 0 ? 16 : 0, display: 'flex', alignItems: 'center', gap: 6, color: '#166534' }}>
                    <ArrowUpFromLine size={15} /> Pick up here
                  </div>
                  {equipChoices.length === 0 && droppedHereExtras.length === 0 && !showAllEquip && (
                    <div style={{ fontSize: 14, color: '#64748b', padding: '0 0 10px' }}>
                      No GPS-tracked equipment at {placeSel?.label} right now.
                    </div>
                  )}
                  {droppedHereExtras.map(it => toggleRow(
                    it.label, 'Dropped here earlier in the route',
                    pickupSel[it.equipment_id as number] !== undefined,
                    () => toggleExtraPickup(it),
                    `extra_${it.equipment_id}`
                  ))}
                  {equipChoices.map(m => toggleRow(
                    labelFor(m),
                    [
                      m.bradanna_equipment_number ? `#${m.bradanna_equipment_number}` : null,
                      m.equipment_type, m.equipment_subtype
                    ].filter(Boolean).join(' · ') + (m.is_rental ? ` · Rental${m.rental_company ? ` (${m.rental_company})` : ''}` : ''),
                    pickupSel[m.equipment_id] !== undefined,
                    () => toggleEquip(m),
                    m.equipment_id
                  ))}
                  {!showAllEquip && (
                    <button onClick={() => { setShowAllEquip(true); loadAllEquipment(); }} style={{ ...secondaryBtn, marginBottom: 12 }}>
                      Show all equipment
                    </button>
                  )}
                  {showAllEquip && allEquipment === null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', padding: 12 }}>
                      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading equipment...
                    </div>
                  )}

                  {/* Custom item */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input
                      value={customText}
                      onChange={e => setCustomText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addCustomItem(); }}
                      placeholder="Other item (trench box, plates...)"
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button onClick={addCustomItem} disabled={!customText.trim()} style={{
                      padding: '0 16px', borderRadius: 8, border: 'none',
                      background: customText.trim() ? '#5b8a8a' : '#d1d5db', color: 'white',
                      fontSize: 15, fontWeight: 700, cursor: customText.trim() ? 'pointer' : 'not-allowed'
                    }}>
                      Add
                    </button>
                  </div>
                  {customPickups.map((c, idx) => (
                    <div key={`${c}_${idx}`} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eef4f4',
                      border: '1px solid #5b8a8a', borderRadius: 999, padding: '6px 12px',
                      fontSize: 14, fontWeight: 600, color: '#1e293b', marginRight: 8, marginBottom: 8
                    }}>
                      {c}
                      <button onClick={() => setCustomPickups(prev => prev.filter((_, i2) => i2 !== idx))}
                        style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, display: 'flex' }}>
                        <X size={16} />
                      </button>
                    </div>
                  ))}

                  {/* Note */}
                  <input
                    value={stopNote}
                    onChange={e => setStopNote(e.target.value)}
                    placeholder="Note for this stop (optional)"
                    style={{ ...inputStyle, marginTop: 8 }}
                  />
                </>
              )}
            </div>

            {/* Pinned action footer — always visible, no scrolling to the bottom */}
            <div style={{ padding: '10px 16px 12px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8 }}>
              {modalStep === 'place' && (
                <button
                  onClick={useCustomPlace}
                  disabled={!customPlaceName.trim()}
                  style={{
                    flex: 1, padding: 14, borderRadius: 10, border: 'none',
                    background: customPlaceName.trim() ? '#5b8a8a' : '#d1d5db', color: 'white',
                    fontSize: 15, fontWeight: 700,
                    cursor: customPlaceName.trim() ? 'pointer' : 'not-allowed'
                  }}
                >
                  Use this place
                </button>
              )}
              {modalStep === 'what' && !editingId && (
                <>
                  <button
                    onClick={() => saveStop()}
                    disabled={actionCount === 0}
                    style={{
                      flex: 1, padding: 14, borderRadius: 10, border: 'none',
                      background: actionCount === 0 ? '#d1d5db' : 'linear-gradient(135deg, #5b8a8a 0%, #4a7373 100%)',
                      color: 'white', fontSize: 16, fontWeight: 700,
                      cursor: actionCount === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Add Stop {actionCount > 0 ? `(${actionCount})` : ''}
                  </button>
                  <button
                    onClick={() => saveStop(true)}
                    disabled={actionCount === 0}
                    style={{
                      padding: '14px 16px', borderRadius: 10,
                      border: actionCount === 0 ? '1px solid #e5e7eb' : '2px solid #5b8a8a',
                      background: 'white', color: actionCount === 0 ? '#d1d5db' : '#3f6363',
                      fontSize: 15, fontWeight: 700,
                      cursor: actionCount === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Add & Next
                  </button>
                </>
              )}
              {modalStep === 'what' && editingId && (
                <button
                  onClick={() => setModalStep('edit')}
                  disabled={actionCount === 0}
                  style={{
                    flex: 1, padding: 14, borderRadius: 10, border: 'none',
                    background: actionCount === 0 ? '#d1d5db' : 'linear-gradient(135deg, #5b8a8a 0%, #4a7373 100%)',
                    color: 'white', fontSize: 16, fontWeight: 700,
                    cursor: actionCount === 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Done {actionCount > 0 ? `(${actionCount})` : ''}
                </button>
              )}
              {modalStep === 'edit' && (
                <button
                  onClick={() => saveStop()}
                  disabled={actionCount === 0 || !placeSel}
                  style={{
                    flex: 1, padding: 14, borderRadius: 10, border: 'none',
                    background: actionCount === 0 || !placeSel ? '#d1d5db' : 'linear-gradient(135deg, #5b8a8a 0%, #4a7373 100%)',
                    color: 'white', fontSize: 16, fontWeight: 700,
                    cursor: actionCount === 0 || !placeSel ? 'not-allowed' : 'pointer'
                  }}
                >
                  Save Changes
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default TruckSchedule;
